/**
 * Activity Tracker Hook
 * Tracks user activity (mouse, keyboard, touch, scroll) and updates status
 */

import { useEffect, useRef, useCallback } from 'react';
import api from '../config/api';

interface UseActivityTrackerOptions {
  enabled?: boolean;
  pingInterval?: number; // How often to ping server (ms)
  awayTimeout?: number; // Time before setting away (ms)
}

export function useActivityTracker(options: UseActivityTrackerOptions = {}) {
  const {
    enabled = true,
    pingInterval = 30000, // 30 seconds
    awayTimeout = 300000 // 5 minutes
  } = options;

  const lastActivityRef = useRef<number>(Date.now());
  const pingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const awayTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isActiveRef = useRef<boolean>(true);

  // Update activity timestamp
  const updateActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
    isActiveRef.current = true;

    // Clear away timeout
    if (awayTimeoutRef.current) {
      clearTimeout(awayTimeoutRef.current);
      awayTimeoutRef.current = null;
    }

    // Set new away timeout
    awayTimeoutRef.current = setTimeout(() => {
      isActiveRef.current = false;
      // Status will be updated to 'away' by server based on last_activity
    }, awayTimeout);
  }, [awayTimeout]);

  // Ping server with activity
  const pingActivity = useCallback(async () => {
    if (!enabled || !isActiveRef.current) return;

    try {
      await api.post('/api/chat/presence/activity');
    } catch (error) {
      console.error('[Activity Tracker] Error pinging activity:', error);
    }
  }, [enabled]);

  // Set up activity listeners
  useEffect(() => {
    if (!enabled) return;

    const events = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'];
    
    events.forEach(event => {
      document.addEventListener(event, updateActivity, { passive: true });
    });

    // Initial activity
    updateActivity();

    // Ping server periodically
    pingActivity(); // Initial ping
    pingIntervalRef.current = setInterval(pingActivity, pingInterval);

    // Cleanup
    return () => {
      events.forEach(event => {
        document.removeEventListener(event, updateActivity);
      });
      
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
      }
      if (awayTimeoutRef.current) {
        clearTimeout(awayTimeoutRef.current);
      }
    };
  }, [enabled, pingInterval, updateActivity, pingActivity]);

  return {
    lastActivity: lastActivityRef.current,
    isActive: isActiveRef.current
  };
}

